import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { dotClass, usd } from "@/features/command/ui";
import { cn } from "@/lib/utils";
import {
  Search, UserPlus, Upload, Download,
  TagIcon, X, BanIcon, Users,
} from "lucide-react";
import {
  Avatar, Badge, Button, EmptyState, Input, Label, Modal, ModalFooter, Select, Skeleton, useToast,
} from "@/components/ui";
import { ContactImportDialog } from "@/features/contacts/ContactImportDialog";
import { NewContactDialog } from "@/features/contacts/NewContactDialog";
import { LeadScoreBadge } from "@/features/contacts/LeadScoreBadge";
import { SmartListPicker } from "@/features/lists/SmartListPicker";
import { SavedViews, type ContactsViewConfig } from "@/features/lists/SavedViews";
import { useSmartList, smartListQueryParams } from "@/features/lists/SmartListContext";
import { api, ApiError } from "@/lib/api";
import { fullName, formatRelative, formatPhone } from "@/lib/format";
import type { Contact, Tag } from "@/lib/types";

export function ContactsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const smartList = useSmartList();
  const { listId, filter, set: setSmartList } = smartList;
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");

  // If the URL has ?list=…, prefer that on first mount (deep-link / share).
  useEffect(() => {
    const urlList = searchParams.get("list");
    if (urlList && urlList !== listId) {
      smartList.set(urlList, { match: "all", rules: [] });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);
  const [sort, setSort] = useState("created_desc");
  // Command Center filters: who (customers / paying / leads / affiliates) and
  // where they came from (a traffic source, incl. its children). Both live
  // in the URL so Traffic → "See all in People" links land filtered.
  const [kind, setKind] = useState<string>(() => searchParams.get("kind") ?? "");
  const [source, setSource] = useState<string>(() => searchParams.get("source") ?? "");
  const [sources, setSources] = useState<Array<{ id: string; name: string; parent_id: string | null }>>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [newOpen, setNewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [tags, setTags] = useState<Tag[]>([]);
  const [page, setPage] = useState(0);
  const limit = 50;

  // Sync listId to URL.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (listId) next.set("list", listId); else next.delete("list");
    setSearchParams(next, { replace: true });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [listId]);

  // Debounce search.
  useEffect(() => {
    const h = setTimeout(() => setDebounced(q), 200);
    return () => clearTimeout(h);
  }, [q]);

  // Load metadata once. Custom fields are no longer needed up front — the
  // new-contact modal is intentionally minimal and the contact-detail page
  // fetches its own field list when the user opens a record.
  useEffect(() => {
    api.get<{ tags: Tag[] }>("/tags").then((d) => setTags(d.tags)).catch(() => {});
    api.get<{ sources: Array<{ id: string; name: string; parent_id: string | null }> }>("/sources", { lite: 1 })
      .then((d) => setSources(d.sources)).catch(() => {});
  }, []);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (kind) next.set("kind", kind); else next.delete("kind");
    if (source) next.set("source", source); else next.delete("source");
    setSearchParams(next, { replace: true });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [kind, source]);

  const adhocActive = (filter.rules?.length ?? 0) > 0;

  const load = useMemo(() => {
    return async (resetSelection = true) => {
      setLoading(true);
      if (resetSelection) setSelected(new Set());
      try {
        const data = await api.get<{ contacts: Contact[]; total: number }>("/contacts", {
          q: debounced || undefined,
          ...smartListQueryParams(listId, filter),
          kind: kind || undefined,
          source: source || undefined,
          sort,
          limit,
          offset: page * limit,
        });
        setContacts(data.contacts);
        setTotal(data.total);
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : "Failed to load contacts");
      } finally {
        setLoading(false);
      }
    };
  }, [debounced, listId, filter, adhocActive, sort, page, kind, source, toast]);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [debounced, listId, filter, sort, page, kind, source]);

  const allChecked = contacts.length > 0 && contacts.every((c) => selected.has(c.id));
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(contacts.map((c) => c.id)));
  };
  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const onSaved = (c: Contact) => {
    setContacts((prev) => {
      const idx = prev.findIndex((x) => x.id === c.id);
      if (idx === -1) return [c, ...prev];
      const next = [...prev]; next[idx] = c; return next;
    });
  };

  const exportCsv = () => {
    const headers = ["first_name", "last_name", "email", "phone", "city", "state", "country", "lifecycle_stage", "source", "created_at"];
    const csv = [
      headers.join(","),
      ...contacts.map((c) => headers.map((h) => csvEscape(String((c as unknown as Record<string, unknown>)[h] ?? ""))).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `contacts-${Date.now()}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // Server order as-is. (The old client-side lead-score sort overlay left
  // with the Score column — sort by score via the Sort select if it ever
  // returns.)
  const displayContacts = contacts;

  // Snapshot everything needed to reconstruct the current view for a saved view.
  const getCurrentConfig = (): ContactsViewConfig => ({ listId, filter, sort, q });

  const applyView = (cfg: ContactsViewConfig) => {
    setSmartList(cfg.listId ?? null, cfg.filter ?? { match: "all", rules: [] });
    setSort(cfg.sort ?? "created_desc");
    setQ(cfg.q ?? "");
    setPage(0);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-50 sm:text-[28px]">People</h1>
          <p className="mt-1 text-sm text-zinc-500">{total.toLocaleString()} {total === 1 ? "person" : "people"}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" leftIcon={<Upload className="h-4 w-4" />} onClick={() => setImportOpen(true)}>
            <span className="hidden sm:inline">Import</span>
          </Button>
          <Button variant="outline" size="sm" leftIcon={<Download className="h-4 w-4" />} onClick={exportCsv}>
            <span className="hidden sm:inline">Export</span>
          </Button>
          <Button leftIcon={<UserPlus className="h-4 w-4" />} onClick={() => setNewOpen(true)}>
            New
          </Button>
        </div>
      </div>

      {/* Who */}
      <div className="flex flex-wrap items-center gap-1.5">
        {[
          { v: "", l: "Everyone" },
          { v: "customers", l: "Have an account" },
          { v: "paying", l: "Paying" },
          { v: "leads", l: "Leads (no account)" },
          { v: "affiliates", l: "Affiliates" },
        ].map((o) => (
          <button
            key={o.v || "all"}
            type="button"
            onClick={() => { setKind(o.v); setPage(0); }}
            className={cn(
              "rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition-colors",
              kind === o.v ? "bg-indigo-500/20 text-indigo-100 ring-indigo-400/40" : "text-zinc-400 ring-zinc-800 hover:text-zinc-100",
            )}
          >
            {o.l}
          </button>
        ))}
        <Select value={source} onChange={(e) => { setSource(e.target.value); setPage(0); }} className="h-8 w-auto text-xs">
          <option value="">Came from: anywhere</option>
          {sources.map((s) => <option key={s.id} value={s.id}>{s.parent_id ? "  · " : ""}{s.name}</option>)}
        </Select>
      </div>

      {/* Filter row */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex-1 sm:max-w-sm">
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            placeholder="Search name, email, phone…"
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
        <SmartListPicker
          listId={listId}
          filter={filter}
          onChange={(lid, f) => { setSmartList(lid, f); setPage(0); }}
        />
        <SavedViews getCurrentConfig={getCurrentConfig} onApply={applyView} />
        <Select value={sort} onChange={(e) => setSort(e.target.value)} className="sm:w-44">
          <option value="created_desc">Newest first</option>
          <option value="created_asc">Oldest first</option>
          <option value="name_asc">Name A→Z</option>
          <option value="name_desc">Name Z→A</option>
          <option value="activity_desc">Last activity</option>
        </Select>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-indigo-500/30 bg-indigo-500/10 px-3 py-2">
          <p className="text-sm text-zinc-200">
            <strong className="font-semibold">{selected.size}</strong> selected
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" leftIcon={<TagIcon className="h-3.5 w-3.5" />} onClick={() => setBulkOpen(true)}>Bulk action</Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : contacts.length === 0 ? (
        <EmptyState
          icon={Users}
          title={q ? "No matching contacts" : adhocActive || listId ? "No contacts match this filter" : "No contacts yet"}
          description={q ? "Try a different search." : "Add your first contact, or import from a CSV."}
          action={
            !q && !adhocActive && !listId && (
              <div className="flex gap-2">
                <Button leftIcon={<UserPlus className="h-4 w-4" />} onClick={() => setNewOpen(true)}>Add contact</Button>
                <Button variant="outline" leftIcon={<Upload className="h-4 w-4" />} onClick={() => setImportOpen(true)}>Import CSV</Button>
              </div>
            )
          }
        />
      ) : (
        <div className="cc-card overflow-hidden">
          {/* Mobile cards */}
          <ul className="divide-y divide-zinc-800/60 sm:hidden">
            {displayContacts.map((c) => (
              <li
                key={c.id}
                onClick={() => navigate(`/contacts/${c.id}`, { state: { from: "/contacts" } })}
                className="flex cursor-pointer items-center gap-3 px-3 py-3 hover:bg-zinc-900/40"
              >
                <Avatar name={fullName(c)} email={c.email} src={c.avatar_url} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-zinc-100">{fullName(c)}</p>
                  <p className="truncate text-xs text-zinc-500">{c.summary?.source_name ? `${c.summary.source_name} · ` : ""}{c.email || formatPhone(c.phone)}</p>
                </div>
                <LeadScoreBadge score={c.lead_score} />
                <input
                  type="checkbox"
                  checked={selected.has(c.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleOne(c.id)}
                  className="h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-indigo-500"
                />
              </li>
            ))}
          </ul>

          {/* Desktop table */}
          {/* Scrolls sideways when the page is narrower than the columns
              (the dashboard's side-by-side view), instead of cutting them off. */}
          <div className="hidden overflow-x-auto sm:block">
          <table className="w-full min-w-[44rem] text-sm">
            <thead>
              <tr className="border-b border-zinc-800/70 bg-zinc-900/40 text-xs uppercase tracking-wider text-zinc-500">
                <th className="w-10 px-3 py-2.5">
                  <input type="checkbox" checked={allChecked} onChange={toggleAll} className="h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-indigo-500" />
                </th>
                <th className="px-3 py-2.5 text-left font-medium">Name</th>
                <th className="px-3 py-2.5 text-left font-medium">Came from</th>
                <th className="px-3 py-2.5 text-left font-medium">SyncSuite</th>
                <th className="hidden px-3 py-2.5 text-left font-medium lg:table-cell">Tags</th>
                <th className="px-3 py-2.5 text-left font-medium">Activity</th>
              </tr>
            </thead>
            <tbody>
              {displayContacts.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/contacts/${c.id}`, { state: { from: "/contacts" } })}
                  className="group cursor-pointer border-b border-zinc-800/40 hover:bg-zinc-900/30"
                >
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleOne(c.id)}
                      className="h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-indigo-500"
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2.5 font-medium text-zinc-100">
                      <Avatar name={fullName(c)} email={c.email} src={c.avatar_url} size="sm" />
                      <div className="min-w-0 max-w-[20rem]">
                        <div className="truncate">{fullName(c)}</div>
                        {(c.email || c.phone) && fullName(c) !== c.email && fullName(c) !== formatPhone(c.phone) && (
                          <div className="truncate text-xs font-normal text-zinc-500" title={c.email ?? undefined}>{c.email || formatPhone(c.phone)}</div>
                        )}
                        {c.do_not_contact && (
                          <div className="flex items-center gap-1 text-[10px] font-normal text-rose-400 mt-0.5">
                            <BanIcon className="h-3 w-3" /> DNC
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-zinc-300">
                    {c.summary?.source_name ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className={cn("h-1.5 w-1.5 rounded-full", dotClass(c.summary.source_color))} />
                        {c.summary.source_name}
                      </span>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    {c.summary?.plan_code ? (
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <span className={cn("rounded-full px-1.5 py-0.5 font-semibold ring-1 ring-inset",
                          Number(c.summary.revenue_usd) > 0 ? "bg-emerald-500/15 text-emerald-200 ring-emerald-500/30" :
                          c.summary.subscription_status === "trialing" ? "bg-amber-500/15 text-amber-200 ring-amber-500/30" :
                          "bg-zinc-800 text-zinc-300 ring-zinc-700")}>
                          {Number(c.summary.revenue_usd) > 0 ? "Paying" : c.summary.subscription_status === "trialing" ? "Trial" : c.summary.plan_code}
                        </span>
                        {Number(c.summary.revenue_usd) > 0 && <span className="tabular-nums text-zinc-400">{usd(Number(c.summary.revenue_usd))}</span>}
                      </span>
                    ) : c.summary?.affiliate_kind ? (
                      <span className="text-xs text-emerald-300">Affiliate</span>
                    ) : <span className="text-xs text-zinc-600">Lead</span>}
                  </td>
                  <td className="hidden px-3 py-2.5 lg:table-cell">
                    <div className="flex max-w-[16rem] flex-nowrap items-center gap-1 overflow-hidden" title={c.tags.map((t) => t.name).join(", ")}>
                      {c.tags.slice(0, 2).map((t) => <Badge key={t.id} color={t.color} size="xs" className="shrink-0">{t.name}</Badge>)}
                      {c.tags.length > 2 && <Badge color="zinc" size="xs" className="shrink-0">+{c.tags.length - 2}</Badge>}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-zinc-500">{c.last_activity_at ? formatRelative(c.last_activity_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && total > limit && (
        <div className="flex items-center justify-between text-sm text-zinc-500">
          <span>Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total.toLocaleString()}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
            <Button variant="outline" size="sm" disabled={(page + 1) * limit >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      {/* Minimal new-contact dialog. Anything beyond name/email/phone is
          edited inline on the contact-detail page so the create flow
          stays a fast 4-field form. */}
      <NewContactDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(c) => { onSaved(c); setNewOpen(false); navigate(`/contacts/${c.id}`, { state: { from: "/contacts" } }); }}
      />
      <ContactImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={() => load()} />
      <BulkActionDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        ids={Array.from(selected)}
        tags={tags}
        onDone={() => { setSelected(new Set()); load(); }}
      />
    </div>
  );
}

function csvEscape(s: string): string {
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

function BulkActionDialog({
  open, onClose, ids, tags, onDone,
}: {
  open: boolean;
  onClose: () => void;
  ids: string[];
  tags: Tag[];
  onDone: () => void;
}) {
  const toast = useToast();
  const [action, setAction] = useState<"add_tag" | "remove_tag" | "set_lifecycle" | "set_do_not_contact" | "delete">("add_tag");
  const [tagName, setTagName] = useState(tags[0]?.name ?? "");
  const [lifecycle, setLifecycle] = useState("lead");
  const [busy, setBusy] = useState(false);
  // Typed-confirmation gate for the delete action. The user has to type
  // the word DELETE before the button enables — the same pattern AWS,
  // GitHub, and Stripe use for irreversible bulk actions. Without this,
  // a single muscle-memory click on "Apply" can wipe thousands of
  // contacts (and all their messages, tasks, notes, and activity).
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const deleteOk = action !== "delete" || deleteConfirm.trim().toUpperCase() === "DELETE";

  // Reset typed-confirm whenever the modal opens or the action changes.
  useEffect(() => { setDeleteConfirm(""); }, [open, action]);

  const submit = async () => {
    if (action === "delete" && !deleteOk) return;
    setBusy(true);
    try {
      const body: Record<string, unknown> = { ids, action };
      if (action === "add_tag" || action === "remove_tag") body.tag_name = tagName;
      if (action === "set_lifecycle") body.lifecycle_stage = lifecycle;
      if (action === "set_do_not_contact") body.do_not_contact = true;
      const res = await api.post<{ updated: number }>("/contacts/bulk", body);
      toast.success(`${res.updated} updated`);
      onDone();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Bulk action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Bulk action on ${ids.length} contacts`} size="md">
      <div className="space-y-3">
        <div>
          <Label>Action</Label>
          <Select value={action} onChange={(e) => setAction(e.target.value as typeof action)}>
            <option value="add_tag">Add tag</option>
            <option value="remove_tag">Remove tag</option>
            <option value="set_lifecycle">Change lifecycle stage</option>
            <option value="set_do_not_contact">Mark as do-not-contact</option>
            <option value="delete">Delete</option>
          </Select>
        </div>
        {(action === "add_tag" || action === "remove_tag") && (
          <div>
            <Label>Tag</Label>
            <Input value={tagName} onChange={(e) => setTagName(e.target.value)} placeholder="VIP" leftIcon={<TagIcon className="h-4 w-4" />} />
          </div>
        )}
        {action === "set_lifecycle" && (
          <div>
            <Label>Stage</Label>
            <Select value={lifecycle} onChange={(e) => setLifecycle(e.target.value)}>
              {["lead", "contact", "customer", "churned"].map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </div>
        )}
        {action === "delete" && (
          <div className="space-y-2 rounded-lg border border-rose-500/30 bg-rose-500/5 p-3">
            <p className="text-sm font-medium text-rose-200">This is irreversible.</p>
            <p className="text-sm text-rose-300/90">
              You're about to permanently delete <strong>{ids.length} contact{ids.length === 1 ? "" : "s"}</strong>
              {" "}and all their messages, notes, tasks, and activity. There's no undo.
            </p>
            <div>
              <Label>Type <code className="rounded bg-rose-500/20 px-1 font-mono text-rose-200">DELETE</code> to confirm</Label>
              <Input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                autoFocus
              />
            </div>
          </div>
        )}
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button
          variant={action === "delete" ? "danger" : "primary"}
          onClick={submit}
          loading={busy}
          disabled={!deleteOk}
        >
          Apply
        </Button>
      </ModalFooter>
    </Modal>
  );
}
